import React, { useContext, useEffect } from 'react';
import {
	useMenuItemDragAndDrop,
	type TMenuItemDragAndDropState,
} from '@atlaskit/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';
import { ContainerAvatar as Avatar } from '@atlaskit/side-nav-items/container-avatar';
import { NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP } from '../common/constants.tsx';
import type { DraggableProjectData } from '../common/types.tsx';
import { FocusRestorationContext } from './focus-restoration-context.tsx';

type UseProjectMenuItemDragAndDropArgs = {
	isEnabled?: boolean;
	projectId: string;
	projectKey: string;
	isFavourite: boolean | null | undefined;
	name: string;
	avatar: {
		large?: string;
	};
};

export const useProjectMenuItemDragAndDrop = (
	props: UseProjectMenuItemDragAndDropArgs,
): {
	state: TMenuItemDragAndDropState;
	draggableButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
	dropTargetRef: React.MutableRefObject<HTMLDivElement | null>;
	draggableAnchorRef: React.MutableRefObject<HTMLAnchorElement | null>;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
	dragPreview: React.ReactElement<any, string | React.JSXElementConstructor<any>> | null;
	dropIndicator: false | JSX.Element | null;
	hasDragIndicator: boolean;
} => {
	const { projectId, projectKey, isFavourite, name, avatar, isEnabled = false } = props;
	/* isEnabled is used to determine if drag and drop functionality should be enabled – this is used to turn on the feature only for starred projects.
	By using this to pass undefined to draggable and dropTarget the useMenuItemDragAndDrop hook does not run its effects to attach event listeners.
	The drag and drop functionality is effectively disabled, while still setting up the refs which are required for consistent rendering. */
	const {
		state,
		draggableButtonRef,
		dropTargetRef,
		draggableAnchorRef,
		dragPreview,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		draggable: isEnabled
			? {
					getInitialData: (): DraggableProjectData => ({
						id: projectId,
						key: projectKey,
						isFavourite,
						name,
						avatar: avatar.large || '',
						type: NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
					}),
					getDragPreviewPieces: () => ({
						elemBefore: <Avatar src={avatar.large || ''} />,
						content: name,
					}),
				}
			: undefined,
		dropTarget: isEnabled
			? {
					getData: (): DraggableProjectData => ({
						id: projectId,
						key: projectKey,
						isFavourite,
						name,
						avatar: avatar.large || '',
						type: NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
					}),
					getOperations: () => ({
						'reorder-after': 'available',
						'reorder-before': 'available',
					}),
					canDrop: ({ source }) => source.data.type === NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
				}
			: undefined,
	});

	const { shouldRestoreFocus, clearRestoreFocus } = useContext(FocusRestorationContext);

	useEffect(() => {
		if (shouldRestoreFocus && isEnabled) {
			// .focus() seems to be more reliable in the next task when used with VoiceOver
			setTimeout(() => {
				(draggableButtonRef.current ?? draggableAnchorRef.current)?.focus();
				clearRestoreFocus();
			});
		}
	}, [clearRestoreFocus, draggableAnchorRef, draggableButtonRef, isEnabled, shouldRestoreFocus]);

	return {
		state,
		draggableButtonRef,
		dropTargetRef,
		draggableAnchorRef,
		dragPreview: isEnabled ? dragPreview : null,
		dropIndicator: isEnabled ? dropIndicator : null,
		hasDragIndicator: isEnabled,
	};
};
